// What a visual / dashboard shows when its data could not be fetched.
//
// The server answers a database it cannot reach with 503 and
// `detail: { code: 'DATA_UNAVAILABLE', message, technical? }` (backend
// `routers/visualdata._raise_failure`): that is not a fault in the visual, so
// it gets a calm notice with Retry, not a red driver message. `technical`
// (host, port, the driver's words) is sent only to the app's builders and sits
// behind "Technical details". Any other failure stays the plain error text it
// always was.

import type { ReactNode } from 'react'
import { dashApplyButtonStyle } from './dashboardChrome'

export const DATA_UNAVAILABLE = 'DATA_UNAVAILABLE'

export interface DataErrorInfo {
  message: string
  /** The data source could not be reached (as opposed to a broken visual). */
  unavailable: boolean
  /** Driver-level text — present only for the people who build the app. */
  technical?: string
}

type Detail = { code?: string; message?: string; technical?: string } | string | undefined

/** Read any thrown fetch error into a DataErrorInfo. */
export function readDataError(e: unknown): DataErrorInfo {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { status?: number; data?: { detail?: Detail; error?: { message?: string } } } }).response
    const detail = resp?.data?.detail
    if (detail && typeof detail === 'object') {
      return {
        message: detail.message || 'The data could not be loaded.',
        unavailable: detail.code === DATA_UNAVAILABLE,
        technical: detail.technical,
      }
    }
    if (typeof detail === 'string') return { message: detail, unavailable: false }
    if (resp?.data?.error?.message) return { message: resp.data.error.message, unavailable: false }
    // No answer body at all: the server itself was not reached.
    if (!resp) {
      return { message: 'The server could not be reached. Check your connection and try again.', unavailable: true }
    }
  }
  return { message: e instanceof Error ? e.message : String(e), unavailable: false }
}

/** A plain error string (a render failure, a client-side check) as DataErrorInfo. */
export function dataErrorOf(message: string): DataErrorInfo {
  return { message, unavailable: false }
}

function DatabaseIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
      <path d="M4 11v6c0 1.66 3.58 3 8 3 1.1 0 2.15-.08 3.1-.24" />
      <path d="M17 17l4 4M21 17l-4 4" />
    </svg>
  )
}

/**
 * The failure, laid over the tile/screen body (same placement as
 * DashboardTileMessage). `onRetry` adds a Try again button.
 */
export function DataErrorMessage({ error, onRetry, children }: {
  error: DataErrorInfo
  onRetry?: () => void
  children?: ReactNode
}) {
  if (!error.unavailable) {
    return (
      <div role="alert" style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, padding: 16, textAlign: 'center', fontSize: 14, color: 'var(--color-error)',
      }}>
        {error.message}
        {children}
      </div>
    )
  }
  return (
    <div role="status" data-tw-state="data-unavailable" style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 10, padding: 20, textAlign: 'center', overflow: 'auto',
    }}>
      <span style={{ color: 'var(--color-text-faint)' }}><DatabaseIcon /></span>
      <div style={{ fontSize: 15, fontWeight: 650, color: 'var(--color-text)' }}>Data not available</div>
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', maxWidth: 440, lineHeight: 1.5 }}>
        {error.message}
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry} style={dashApplyButtonStyle()}>Try again</button>
      )}
      {error.technical && (
        <details style={{ maxWidth: 520, fontSize: 13, color: 'var(--color-text-faint)' }}>
          <summary style={{ cursor: 'pointer' }}>Technical details</summary>
          <div style={{ marginTop: 6, textAlign: 'left', wordBreak: 'break-word', fontFamily: 'var(--font-mono, monospace)' }}>
            {error.technical}
          </div>
        </details>
      )}
      {children}
    </div>
  )
}
